import React, { useState } from 'react';
import { LayoutDashboard, Users, Package, Building2, Briefcase, Headset, X, Settings, FileSearch, ClipboardList } from 'lucide-react';
import { MenuItem, PageId, Role } from '@/types';
import { useServices } from '@/contexts/ServicesContext';

interface SidebarProps {
  role: Role;
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
  darkMode: boolean;
  tenantName?: string;
  logoUrl?: string;
  isOpen: boolean;
  onClose: () => void;
}

const MENU_ITEMS: MenuItem[] = [
  // Sempre visíveis
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'staff', 'client'] },
  // Admin Only - Logo após Dashboard
  { id: 'usuarios', label: 'Usuários', icon: Users, roles: ['admin'] },
  
  // Admin/Staff (Produtos)
  { id: 'produtos', label: 'Produtos', icon: Package, roles: ['admin', 'staff'] },
  
  // Condicionais por Serviço
  { id: 'imobiliarias', label: 'Imobiliárias', icon: Building2, roles: ['admin', 'staff'], visibleWhen: ['COORDENACAO'] },
  { id: 'corretores', label: 'Corretores', icon: Briefcase, roles: ['admin', 'staff'], visibleWhen: ['COORDENACAO', 'GESTAO'] },
  { id: 'atendimento', label: 'Atendimento', icon: Headset, roles: ['admin', 'staff'], visibleWhen: ['COORDENACAO'] },
  { id: 'pesquisas', label: 'Pesquisas', icon: FileSearch, roles: ['admin', 'staff'], visibleWhen: ['PESQUISA'] },

  // Admin Only - Configurações por último
  { id: 'configuracoes', label: 'Configurações', icon: Settings, roles: ['admin'] },
];

export const Sidebar: React.FC<SidebarProps> = ({ 
  role, 
  currentPage, 
  onNavigate, 
  darkMode,
  tenantName,
  logoUrl,
  isOpen,
  onClose 
}) => {
  const { activeServices } = useServices();
  const [logoError, setLogoError] = useState(false);
  const showLogo = Boolean(logoUrl) && !logoError;
  
  // Filter items based on user role AND active services
  const visibleItems = MENU_ITEMS.filter(item => {
    // 1. Check Role
    if (!item.roles.includes(role)) return false;

    // 2. Check Service visibility (if defined)
    if (item.visibleWhen) {
      // Admin bypass service check? Maybe not, as admin sees everything configured. 
      // But spec says: "Condicionais por serviço".
      // Let's assume Admin also respects active services context, 
      // or if activeServices is empty (initial load), show nothing or defaults?
      // The mock in ServicesContext initializes with ['RELATORIOS', 'COORDENACAO', 'GESTAO'].
      
      return item.visibleWhen.some(svc => activeServices.includes(svc));
    }

    return true;
  });

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      {/* Sidebar Container */}
      <aside className={`
        fixed inset-y-0 left-0 z-40 w-64 flex flex-col transition-transform duration-300 ease-in-out border-r md:relative md:translate-x-0
        ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo Area */}
        <div className={`h-16 md:h-20 flex items-center justify-center border-b px-4 ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            {showLogo ? (
              <div className={`rounded-lg px-3 py-2 ${darkMode ? 'bg-gradient-to-br from-white/90 via-white/70 to-white/40' : ''}`}>
                <img
                  src={logoUrl}
                  alt={tenantName ? `Logo ${tenantName}` : "Logo do tenant"}
                  className="h-12 md:h-14 w-auto object-contain"
                  onError={() => setLogoError(true)}
                />
              </div>
            ) : (
              <>
                <img
                  src="/company-logo.png"
                  alt="Logo da empresa"
                  className="h-10 md:h-12 w-auto object-contain opacity-90"
                />
              </>
            )}
          </div>
          {/* Mobile Close Button */}
          <button 
            onClick={onClose}
            className={`md:hidden p-2 rounded-full ${darkMode ? 'hover:bg-vision-surface text-white' : 'hover:bg-gray-100 text-gray-600'}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1">
            {visibleItems.map((item) => {
              const isActive = currentPage === item.id;
              const Icon = item.icon;

              return (
                <li key={item.id}>
                  <button
                    onClick={() => onNavigate(item.id)}
                    className={`
                      w-full flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors duration-200 border-l-4
                      ${isActive 
                        ? darkMode 
                          ? 'bg-vision-activeRed border-red-500 text-white shadow-md' 
                          : 'bg-[#294b29] border-[#1a301a] text-white shadow-md'
                        : darkMode 
                          ? 'border-transparent text-gray-400 hover:bg-vision-surface hover:text-white' 
                          : 'border-transparent text-vision-green hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer / Copyright area */}
        <div className={`p-4 text-xs text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          <div className="mb-2 flex items-center justify-center">
            <img
              src="/company-logo.png"
              alt="Logo da empresa"
              className="h-5 w-auto opacity-70"
            />
          </div>
          &copy; Visao Alpha System 1.0
        </div>
      </aside>
    </>
  );
};




